import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, apiUrl, ApiError, resolveTenantSlug, tokens } from '../lib/api';

/**
 * ═══ BULK IMPORT — ONE SCREEN, USED BY STUDENTS AND BY STAFF ═══
 *
 * ⚠ THE PROBLEM REPORT IS THE SCREEN. The upload box is trivial; what makes a principal willing to
 * hand over their entire student list is being shown, before anything is written, which of their
 * 640 rows are wrong and why — with row numbers matching what Excel shows in the margin.
 *
 * ⚠ AND NOTHING IS WRITTEN UNTIL THEY PRESS THE SECOND BUTTON. The dry run is a separate request
 * that cannot write; the commit re-sends the same file and the server re-validates from scratch.
 *
 * ⚠ THIS WAS ONE PAGE AND IS NOW TWO USES OF ONE COMPONENT, deliberately rather than by copy. Every
 * detail below was learned from something going wrong — the token on the template download, the
 * blob URL revoke, the cap on the problem table, the refusal to do arithmetic in the summary
 * sentence. A second copy would agree with the first today and drift by November, and the school
 * that gets the drifted one is the one importing 45 teachers rather than 640 children.
 */

export interface Problem {
  row: number;
  column: string;
  message: string;
  severity: 'Error' | 'Warning';
}

export interface Preview {
  readyCount: number;
  errorCount: number;
  warningCount: number;
  canCommit: boolean;
  problems: Problem[];
}

export interface Result {
  batchId: string;
  imported: number;
}

export interface BulkImportConfig<P extends Preview> {
  /** "Students" / "Staff" — the eyebrow above the title. */
  eyebrow: string;
  title: string;
  intro: ReactNode;
  /** e.g. "/api/students/import" */
  basePath: string;
  templateFileName: string;
  /** What is in the file, singular and plural: "student" / "students". */
  noun: string;
  nounPlural: string;
  /** What the second card says about which columns are required. */
  uploadHint: ReactNode;
  /** Where the imported records now live. */
  listTo: string;
  listLabel: string;
  /** Anything this particular import wants to show alongside the counts. */
  extraSummary?: (preview: P) => ReactNode;
}

export function BulkImport<P extends Preview>({ config }: { config: BulkImportConfig<P> }) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<P | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState<'' | 'checking' | 'importing' | 'undoing'>('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const send = useCallback(async (path: string) => {
    const form = new FormData();
    form.append('file', file!);
    return api.postForm(`${config.basePath}/${path}`, form);
  }, [file, config.basePath]);

  const pick = (f: File | null) => {
    // ⚠ Everything downstream is cleared with the file. A report from the PREVIOUS spreadsheet
    // sitting under a newly chosen one is how somebody commits a file they never checked.
    setFile(f); setPreview(null); setResult(null); setError(null);
  };

  const check = async () => {
    setBusy('checking'); setError(null); setResult(null);
    try { setPreview(await send('dry-run') as P); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not read that file.'); }
    finally { setBusy(''); }
  };

  const commit = async () => {
    setBusy('importing'); setError(null);
    try { setResult(await send('commit') as Result); setPreview(null); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'The import failed.'); }
    finally { setBusy(''); }
  };

  const undo = async () => {
    if (!result) return;
    setBusy('undoing'); setError(null);
    try {
      await api.post(`${config.basePath}/${result.batchId}/undo`, {});
      setResult(null); setFile(null); if (inputRef.current) inputRef.current.value = '';
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not undo.'); }
    finally { setBusy(''); }
  };

  // ⚠ A fetch, not an <a href>. The route needs the bearer token, and a plain link's request is
  // issued by the browser without headers — the school would download a 401 page named .csv.
  const downloadTemplate = async () => {
    const slug = resolveTenantSlug();
    const res = await fetch(apiUrl(`${config.basePath}/template`), {
      headers: {
        ...(slug ? { 'X-Tenant': slug } : {}),
        ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}),
      },
    });
    const url = URL.createObjectURL(await res.blob());
    try {
      const a = document.createElement('a');
      a.href = url; a.download = config.templateFileName; a.click();
    } finally { URL.revokeObjectURL(url); }
  };

  const errors = preview?.problems.filter((p) => p.severity === 'Error') ?? [];
  const warnings = preview?.problems.filter((p) => p.severity === 'Warning') ?? [];
  const plural = (n: number) => (n === 1 ? config.noun : config.nounPlural);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>{config.eyebrow}</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>{config.title}</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4, maxWidth: '68ch' }}>
          {config.intro}
        </p>
      </div>

      {/* ── step 1 ── */}
      <div className="card" style={{ padding: 18, marginBottom: 12 }}>
        <div style={{ fontWeight: 650, marginBottom: 4 }}>1 · Start from the template</div>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 12px', maxWidth: '62ch' }}>
          It has the right headings and one example row showing the date format. Delete the example
          or leave it — Eduvora skips it either way.
        </p>
        <button className="btn" onClick={downloadTemplate}>Download the template</button>
      </div>

      {/* ── step 2 ── */}
      <div className="card" style={{ padding: 18, marginBottom: 12 }}>
        <div style={{ fontWeight: 650, marginBottom: 4 }}>2 · Upload it back</div>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 12px', maxWidth: '62ch' }}>
          {config.uploadHint}
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={inputRef} type="file" accept=".csv,text/csv" disabled={busy !== ''}
            onChange={(e) => pick(e.target.files?.[0] ?? null)} />
          <button className="btn btn-primary" disabled={!file || busy !== ''} onClick={check}>
            {busy === 'checking' ? 'Checking…' : 'Check the file'}
          </button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ padding: 14, marginBottom: 12, color: 'var(--danger)', fontSize: 13.5 }}>
          {error}
        </div>
      )}

      {/* ── step 3: the report ── */}
      {preview && (
        <div className="card" style={{ padding: 18, marginBottom: 12 }}>
          <div style={{ fontWeight: 650, marginBottom: 10 }}>3 · What will happen</div>

          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 14 }}>
            <Stat n={preview.readyCount} label="ready to import" />
            <Stat n={preview.errorCount} label="must be fixed" tone={preview.errorCount ? 'bad' : undefined} />
            <Stat n={preview.warningCount} label="worth a look" tone={preview.warningCount ? 'warn' : undefined} />
          </div>

          {config.extraSummary?.(preview)}

          {/* ⚠ Says WHY nothing can be imported, not just that the button is off. A disabled button
              with no sentence beside it is the most common way software refuses without explaining. */}
          {/* ⚠ NO ARITHMETIC IN THIS SENTENCE. The first version printed readyCount + errorCount as
              a row total, which is wrong: errorCount counts PROBLEMS and one row can carry three of
              them. A confidently stated wrong number, on the screen a principal is using to decide
              whether to trust us with their list, is worse than no number. */}
          {preview.errorCount > 0 && (
            <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 12px', maxWidth: '66ch' }}>
              Nothing will be imported while there are errors — the whole file lands together or not
              at all. Fix the rows below in your spreadsheet and upload it again.
            </p>
          )}

          {errors.length > 0 && <ProblemTable title="Errors" rows={errors} tone="bad" />}
          {warnings.length > 0 && <ProblemTable title="Warnings — these still import" rows={warnings} tone="warn" />}

          {preview.problems.length === 0 && (
            <p style={{ fontSize: 13.5, color: 'var(--ok, var(--ink-2))', margin: '0 0 12px' }}>
              Every row is clean.
            </p>
          )}

          <button className="btn btn-primary" disabled={!preview.canCommit || busy !== ''} onClick={commit}>
            {busy === 'importing'
              ? 'Importing…'
              : `Import ${preview.readyCount} ${plural(preview.readyCount)}`}
          </button>
        </div>
      )}

      {/* ── done ── */}
      {result && (
        <div className="card" style={{ padding: 18, borderLeft: '4px solid var(--ok, var(--accent))' }}>
          <div style={{ fontWeight: 650, marginBottom: 4 }}>
            {result.imported} {plural(result.imported)} imported
          </div>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 12px', maxWidth: '62ch' }}>
            They are on the <Link className="link" to={config.listTo}>{config.listLabel}</Link> now.
            If this was the wrong file you can undo it — while these records are still untouched.
          </p>
          {/* ⚠ Undo is offered, and the SERVER decides whether it is still safe. Once a fee, a
              register or a payslip exists against an imported record, deleting it would destroy
              something real, and the refusal explains that rather than this screen guessing. */}
          <button className="btn" disabled={busy !== ''} onClick={undo}>
            {busy === 'undoing' ? 'Undoing…' : 'Undo this import'}
          </button>
        </div>
      )}
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone?: 'bad' | 'warn' }) {
  const colour = tone === 'bad' ? 'var(--danger)' : tone === 'warn' ? 'var(--warn)' : 'var(--ink)';
  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 650, color: colour, lineHeight: 1.1 }}>{n}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{label}</div>
    </div>
  );
}

/**
 * ⚠ ROW NUMBER FIRST, AND IT MATCHES EXCEL'S MARGIN. Somebody is going to read this on one screen
 * and fix the spreadsheet on another; anything that makes them count rows by hand has failed.
 */
function ProblemTable({ title, rows, tone }: { title: string; rows: Problem[]; tone: 'bad' | 'warn' }) {
  // ⚠ Capped, with the remainder stated. A file where every row is wrong — the wrong file entirely,
  // usually — would otherwise render six hundred rows and bury the button.
  const shown = rows.slice(0, 50);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 12.5, fontWeight: 650, marginBottom: 6,
        color: tone === 'bad' ? 'var(--danger)' : 'var(--warn)',
      }}>{title}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={th}>Row</th><th style={th}>Column</th><th style={th}>What is wrong</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((p, i) => (
            <tr key={i}>
              <td style={{ ...td, width: 56, fontVariantNumeric: 'tabular-nums' }}>{p.row || '—'}</td>
              <td style={{ ...td, width: 150, color: 'var(--ink-2)' }}>{p.column || '—'}</td>
              <td style={td}>{p.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > shown.length && (
        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
          …and {rows.length - shown.length} more. Fix these first — the same mistake is often repeated.
        </p>
      )}
    </div>
  );
}

const th: React.CSSProperties = {
  textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--line)',
  fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--ink-3)',
};
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid var(--line-2)', verticalAlign: 'top' };
